<template>
  <el-dialog
    :title="getDialogTitle()"
    :visible.sync="dialogVisible"
    width="1000px"
    append-to-body
    custom-class="material-dialog"
  >
    <div class="material-dialog-content">
      <el-row :gutter="20">
        <!-- 左侧分类列表 -->
        <el-col :span="6">
          <div class="category-list-container">
            <div class="category-list-header">分类列表</div>
            <div class="category-list-body">
              <el-menu
                :default-active="activeCategory"
                class="category-menu"
                @select="handleCategorySelect"
              >
                <el-menu-item
                  v-for="category in categories"
                  :key="category._id"
                  :index="category._id"
                >
                  <span>{{ category.name }}</span>
                  <span class="count">({{ category.totalMaterials || 0 }})</span>
                </el-menu-item>
              </el-menu>
            </div>
          </div>
        </el-col>
        
        <!-- 右侧图片列表 -->
        <el-col :span="18">
          <div class="image-list-container">
            <div class="image-list-header">
              <span>{{ currentCategory ? currentCategory.name : `全部${getResourceType()}` }}</span>
              <div class="selection-info">
                <span>已选择: {{ selectedImages.length }} 个</span>
                <el-button type="text" size="small" @click="selectedImages = []">清空</el-button>
              </div>
            </div>
            <div class="image-list-body">
              <template v-if="currentMaterials.length > 0">
                <el-checkbox-group v-model="selectedImages" class="image-grid">
                  <el-card
                    v-for="material in currentMaterials"
                    :key="material._id"
                    class="image-card"
                    :class="{ 'is-selected': selectedImages.includes(material._id) }"
                    shadow="hover"
                    @click.native="toggleSelection(material._id)"
                  >
                    <div class="image-container">
                      <!-- 根据不同素材类型显示不同内容 -->
                      <template v-if="dialogData.type === 'video'">
                        <img 
                          :src="material.coverUrl || material.cover || '//placeholder.com/150x100'" 
                          :alt="material.title" 
                          class="image" 
                        />
                        <i class="el-icon-video-play video-icon"></i>
                      </template>
                      <template v-else-if="dialogData.type === 'document'">
                        <div class="document-icon-container">
                          <i :class="getDocumentIconClass(material)" class="document-icon"></i>
                        </div>
                      </template>
                      <template v-else>
                        <img 
                          :src="material.content || material.url" 
                          :alt="material.title" 
                          class="image"
                          @error="handleImageError($event)" 
                        />
                      </template>
                      
                      <!-- 将选择框隐藏但保留功能 -->
                      <el-checkbox 
                        :label="material._id" 
                        class="image-checkbox"
                        v-show="false"
                      ></el-checkbox>
                      
                      <!-- 显示素材标题 -->
                      <div class="material-title">{{material.title}}</div>
                    </div>
                  </el-card>
                </el-checkbox-group>
              </template>
              <el-empty v-else :description="`该分类下暂无${getResourceType()}`"></el-empty>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" @click="confirmSelection">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getSubCategories } from '@/api/material'

export default {
  name: 'MaterialDialog',
  
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    dialogData: {
      type: Object,
      default: () => ({
        type: 'image',
        multiple: false,
        max: 1,
        selected: []
      })
    }
  },
  
  data() {
    return {
      dialogVisible: this.visible,
      categories: [],
      activeCategory: '',
      selectedImages: []
    }
  },
  
  computed: {
    currentCategory() {
      return this.categories.find(category => category._id === this.activeCategory) || null
    },
    
    currentMaterials() {
      if (!this.currentCategory) return []
      return this.currentCategory.materials || []
    }
  },
  
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        // 当对话框打开时，初始化已选图片
        this.selectedImages = this.dialogData.selected || []
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  
  created() {
    this.fetchCategories()
  },
  
  methods: {
    getDialogTitle() {
      const typeMap = {
        image: '选择图片',
        video: '选择视频',
        document: '选择文档',
        article: '选择文章'
      }
      return typeMap[this.dialogData.type] || '选择素材'
    },
    
    async fetchCategories() {
      try {
        // 根据素材类型确定分类ID
        let categoryId = '670e74efaf089a40acf16ad9'; // 默认为图片分类ID
        
        // 如果传入了特定的分类ID，则使用传入的值
        if (this.dialogData && this.dialogData.categoryId) {
          categoryId = this.dialogData.categoryId;
          console.log('使用传入的分类ID:', categoryId);
        } else {
          // 根据素材类型自动选择分类ID
          if (this.dialogData.type === 'video') {
            categoryId = '670e74efaf089a40acf16adb'; // 视频分类ID
          } else if (this.dialogData.type === 'document') {
            categoryId = '670e74efaf089a40acf16adc'; // 文档分类ID
          }
        }
        
        const response = await getSubCategories({
          categoryId: categoryId,
          page: 1,
          pageSize: 100
        })
        
        console.log('获取分类结果:', {
          type: this.dialogData.type,
          categoryId: categoryId,
          status: response?.status,
          categories: response?.data?.length || 0
        });
        
        if (response && response.status === '10000' && Array.isArray(response.data)) {
          this.categories = response.data
          
          // 设置默认选中的分类（第一个有素材的分类）
          if (this.categories.length > 0) {
            const firstCategory = this.categories.find(cat => (cat.totalMaterials || 0) > 0) || this.categories[0]
            this.activeCategory = firstCategory._id
          }
          
          // 初始化已选图片
          this.selectedImages = this.dialogData.selected || []
        } else {
          this.$message.error(response?.message || '获取分类失败')
        }
      } catch (error) {
        console.error('获取分类失败:', error)
        this.$message.error('获取分类失败，请稍后重试')
      }
    },
    
    handleCategorySelect(index) {
      this.activeCategory = index
    },
    
    confirmSelection() {
      if (this.selectedImages.length === 0) {
        this.$message.warning(`请至少选择一个${this.getResourceType()}`)
        return
      }
      
      if (this.dialogData.max && this.selectedImages.length > this.dialogData.max) {
        this.$message.warning(`最多只能选择 ${this.dialogData.max} 个${this.getResourceType()}`)
        return
      }
      
      // 收集选中的素材
      const selectedMaterials = []
      
      // 从所有分类中查找选中的素材
      this.categories.forEach(category => {
        if (category.materials) {
          const matchedMaterials = category.materials.filter(material => 
            this.selectedImages.includes(material._id)
          )
          
          matchedMaterials.forEach(material => {
            // 先记录原始素材信息
            console.log('原始素材信息:', { 
              id: material._id, 
              title: material.title,
              所有字段: Object.keys(material)
            });
            
            // 根据不同的素材类型准备不同的数据结构
            let preparedMaterial = {
              _id: material._id,
              id: material._id, // 添加id字段以兼容部分组件
              title: material.title,
              description: material.description || material.summary || '',
              publishTime: material.publishTime || material.createTime || new Date().toISOString()
            };
            
            // 根据素材类型添加特定字段
            if (this.dialogData.type === 'image') {
              preparedMaterial = {
                ...preparedMaterial,
                url: material.url || material.content || '',
                content: material.content || material.url || '',
                cover: material.cover || material.content || ''
              };
              
              console.log('处理图片材料:', {
                id: preparedMaterial.id,
                title: preparedMaterial.title,
                url: preparedMaterial.url,
                content: preparedMaterial.content
              });
            } else if (this.dialogData.type === 'video') {
              preparedMaterial = {
                ...preparedMaterial,
                content: material.content || '',
                url: material.videoUrl || material.url || material.content || '',
                cover: material.coverUrl || material.cover || '',
                poster: material.coverUrl || material.cover || ''
              };
            } else if (this.dialogData.type === 'document') {
              // 先完整记录原始值
              console.log('原始文档字段:', {
                _id: material._id,
                title: material.title,
                url: material.url,
                fileUrl: material.fileUrl,
                content: material.content,
                所有可用字段: Object.keys(material)
              });
              
              // 确保保留原始标题
              preparedMaterial = {
                ...preparedMaterial,
                url: material.url || '',
                content: material.content || '',
                fileUrl: material.fileUrl || material.url || material.content || '',
                size: material.size || 0,
                fileType: material.fileType || this.getFileExtension(material.url || material.fileUrl || material.content || '')
              };
              
              console.log('处理后的文档材料:', {
                id: preparedMaterial.id,
                title: preparedMaterial.title,
                url: preparedMaterial.url,
                content: preparedMaterial.content,
                fileUrl: preparedMaterial.fileUrl,
                size: preparedMaterial.size
              });
            } else if (this.dialogData.type === 'article') {
              preparedMaterial = {
                ...preparedMaterial,
                url: material.url || material.fileUrl || '',
                content: material.content || material.summary || '',
                cover: material.coverUrl || material.cover || ''
              };
            }
            
            selectedMaterials.push(preparedMaterial)
          })
        }
      })
      
      console.log(`返回的${this.getResourceType()}:`, selectedMaterials)
      this.$emit('select', selectedMaterials)
      this.dialogVisible = false
    },
    
    getResourceType() {
      const typeMap = {
        image: '图片',
        video: '视频',
        document: '文档',
        article: '文章'
      }
      return typeMap[this.dialogData.type] || '素材'
    },
    
    getDocumentIconClass(material) {
      if (!material) return 'el-icon-folder';
      
      // 检查所有可能包含URL的字段
      const url = material.url || material.fileUrl || material.content || '';
      if (!url) return 'el-icon-folder';
      
      // 提取文件后缀名并处理特殊情况（如URL带参数）
      let ext = '';
      if (url.includes('.')) {
        ext = url.split('.').pop().toLowerCase();
        // 去除可能的URL参数
        if (ext.includes('?')) {
          ext = ext.split('?')[0];
        }
        // 去除可能的URL片段
        if (ext.includes('#')) {
          ext = ext.split('#')[0];
        }
      }
      
      if (!ext) return 'el-icon-folder';
      
      const iconMap = {
        'pdf': 'el-icon-document-checked',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-s-grid',
        'xlsx': 'el-icon-s-grid',
        'ppt': 'el-icon-picture-outline',
        'pptx': 'el-icon-picture-outline',
        'txt': 'el-icon-document',
        'zip': 'el-icon-folder-opened',
        'rar': 'el-icon-folder-opened'
      };
      
      return iconMap[ext] || 'el-icon-folder';
    },
    
    getFileTypeLabel(material) {
      if (!material) return '未知类型';
      
      const url = material.url || material.fileUrl || material.content || '';
      if (!url) return '未知类型';
      
      // 提取文件后缀名并处理特殊情况（如URL带参数）
      let ext = '';
      if (url.includes('.')) {
        ext = url.split('.').pop().toLowerCase();
        // 去除可能的URL参数
        if (ext.includes('?')) {
          ext = ext.split('?')[0];
        }
        // 去除可能的URL片段
        if (ext.includes('#')) {
          ext = ext.split('#')[0];
        }
      }
      
      if (!ext) return '未知类型';
      
      const typeMap = {
        'pdf': 'PDF文档',
        'doc': 'Word文档',
        'docx': 'Word文档',
        'xls': 'Excel表格',
        'xlsx': 'Excel表格',
        'ppt': 'PPT演示',
        'pptx': 'PPT演示',
        'txt': '文本文档',
        'zip': '压缩文件',
        'rar': '压缩文件'
      };
      
      return typeMap[ext] || ext.toUpperCase() + '文件';
    },
    
    formatFileSize(size) {
      if (!size) return '';
      size = Number(size);
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return size.toFixed(1) + ' ' + units[i];
    },
    
    handleImageError($event) {
      // 处理图片加载错误后的逻辑
      console.error('图片加载失败:', $event)
      // 可以添加一些默认图片或错误处理逻辑
    },
    
    toggleSelection(id) {
      const index = this.selectedImages.indexOf(id);
      if (index === -1) {
        this.selectedImages.push(id);
      } else {
        this.selectedImages.splice(index, 1);
      }
    },
    
    getFileExtension(url) {
      if (!url) return '';
      
      // 如果URL中不包含点号，不是有效的文件URL
      if (!url.includes('.')) return '';
      
      try {
        // 尝试从URL中提取文件名
        let filename = url.split('/').pop();
        
        // 去除可能存在的URL参数
        if (filename.includes('?')) {
          filename = filename.split('?')[0];
        }
        
        // 去除可能存在的URL片段
        if (filename.includes('#')) {
          filename = filename.split('#')[0];
        }
        
        // 从文件名中提取扩展名
        const ext = filename.split('.').pop().toLowerCase();
        
        console.log('从URL提取的文件扩展名:', {
          url: url,
          filename: filename,
          extension: ext
        });
        
        return ext;
      } catch (error) {
        console.error('提取文件扩展名错误:', error);
        return '';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.material-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
  }
}

.material-dialog-content {
  min-height: 500px;
}

.category-list-container, .image-list-container {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  height: 100%;
}

.category-list-header, .image-list-header {
  padding: 10px 15px;
  font-size: 14px;
  font-weight: bold;
  border-bottom: 1px solid #ebeef5;
  background-color: #f5f7fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #409EFF;
}

.category-list-body {
  height: 450px;
  overflow-y: auto;
}

.category-menu {
  border-right: none;
}

.count {
  margin-left: 5px;
  color: #909399;
  font-size: 12px;
}

.image-list-body {
  height: 450px;
  overflow-y: auto;
  padding: 15px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  padding: 15px;
}

.image-card {
  position: relative;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
  
  &.is-selected {
    border-color: #409EFF;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(64, 158, 255, 0.1);
      z-index: 1;
    }
    
    .document-icon-container {
      background-color: rgba(64, 158, 255, 0.2);
    }
    
    .document-icon {
      color: #409EFF;
      opacity: 1;
    }
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
}

.image-container {
  position: relative;
  width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  
  .image {
    width: 100%;
    height: 140px;
    object-fit: cover;
    object-position: center;
  }
  
  .material-title {
    margin-top: 8px;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    color: #333;
    padding: 0 4px;
  }
  
  .image-checkbox {
    position: absolute;
    right: 5px;
    top: 5px;
    z-index: 2;
  }
  
  .video-icon,
  .document-icon {
    position: absolute;
    top: 50px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 36px;
    color: #fff;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    z-index: 2;
  }
}

.document-icon-container {
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f7fa;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
}

.document-info {
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
  text-align: center;
  background-color: rgba(245, 247, 250, 0.7);
  
  .file-type {
    font-size: 12px;
    color: #606266;
    margin-bottom: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .file-size {
    font-size: 11px;
    color: #909399;
  }
}

.document-icon-container .document-icon {
  position: static;
  transform: none;
  font-size: 60px;
  color: #409EFF;
  text-shadow: none;
  opacity: 0.8;
  transition: all 0.3s ease;
}
</style> 